import React from "react";
import { Navigate, type RouteObject } from "react-router-dom";
import App from "./App";
import NotFoundPage from "./pages/not-found";
import { RouteError } from "./components/route-error";
import { RouteFallback } from "./components/route-fallback";
import { KnotsPanel, LoopsPanel, StringsPanel } from "./pages/y/panels";
import { SETTINGS_SECTIONS, settingsPath } from "./lib/app-paths";

/**
 * Everything behind the marketing page loads on demand. `App` and the
 * not-found screen stay static — they are what a first-time visitor renders,
 * and a chunk request for them would only add a round trip.
 */
const lazyPage = <P extends { default: React.ComponentType }>(
  load: () => Promise<P>
) => React.lazy(load as () => Promise<{ default: React.ComponentType }>);

const LoginPage = lazyPage(() =>
  import("./pages/login/index").then((m) => ({ default: m.LoginPage }))
);
const SignUpPage = lazyPage(() =>
  import("./pages/signup/index").then((m) => ({ default: m.SignUpPage }))
);
const SignUpPhonePage = lazyPage(() =>
  import("./pages/signup-phone/index").then((m) => ({
    default: m.SignUpPhonePage,
  }))
);
const ResetPasswordPage = lazyPage(() =>
  import("./pages/resetpassword/index").then((m) => ({
    default: m.ResetPasswordPage,
  }))
);
const ForgotPasskeyPage = lazyPage(() =>
  import("./pages/forgot-passkey/index").then((m) => ({
    default: m.ForgotPasskeyPage,
  }))
);

const MenuLayout = lazyPage(() => import("./pages/y/layout"));
const NewStringPage = lazyPage(() => import("./pages/y/strings/new"));
const StringChatPage = lazyPage(() => import("./pages/y/strings/chat"));
const CreateKnotPage = lazyPage(() => import("./pages/y/knots/create"));
const KnotChatPage = lazyPage(() => import("./pages/y/knots/[id]/chat"));
const KnotInfoPage = lazyPage(() => import("./pages/y/knots/[id]/info"));
const NewLoopPage = lazyPage(() => import("./pages/y/loops/new"));
const LoopChatPage = lazyPage(() => import("./pages/y/loops/[id]/index"));
const LoopChatRedirect = lazyPage(() => import("./pages/y/loops/[id]/chat"));
const BraneSendPage = lazyPage(() => import("./pages/y/b/send"));
const InboxPage = lazyPage(() => import("./pages/y/inbox/index"));
const NotificationLayout = lazyPage(() => import("./pages/y/notifications/layout"));
const SettingsLayout = lazyPage(() => import("./pages/y/settings/layout"));
const ProfileSettings = lazyPage(() => import("./pages/y/settings/profile/page"));
const BillingSettings = lazyPage(() => import("./pages/y/settings/billings/page"));
const BraneSettings = lazyPage(() => import("./pages/y/settings/branes/page"));
const DeviceSettings = lazyPage(() => import("./pages/y/settings/devices/page"));
const BlockedSettings = lazyPage(() => import("./pages/y/settings/blocked/page"));
const AppearanceSettings = lazyPage(() => import("./pages/y/settings/appearance/page"));
const NotificationsForm = lazyPage(() =>
  import("./pages/y/settings/notifications/notification-form").then((m) => ({
    default: m.NotificationsForm,
  }))
);
const BranePage = lazyPage(() => import("./pages/b/index"));
const KnotPage = lazyPage(() => import("./pages/k/index"));
const AboutPage = lazyPage(() => import("./pages/about/index"));
const FaqPage = lazyPage(() => import("./pages/faq/index"));

const TokenGallery = import.meta.env.DEV
  ? lazyPage(() => import("./pages/dev/tokens"))
  : null;
const HierarchyHarness = import.meta.env.DEV
  ? lazyPage(() => import("./pages/dev/hierarchy"))
  : null;
const LoopsHarness = import.meta.env.DEV
  ? lazyPage(() => import("./pages/dev/loops"))
  : null;
const AppHarness = import.meta.env.DEV
  ? lazyPage(() => import("./pages/dev/app/index"))
  : null;

/**
 * A lazy route element. Each route gets its own `Suspense` rather than one
 * around the whole tree, so a chunk still loading for the room never suspends
 * the nav shell that is already on screen.
 */
const page = (Component: React.ComponentType) => (
  <React.Suspense fallback={<RouteFallback />}>
    <Component />
  </React.Suspense>
);

/**
 * `errorElement` sits on the routes *inside* `/y`, not on `/y` itself, so a
 * crash in a room renders into `MenuLayout`'s `<Outlet/>` and the user keeps
 * their navigation. The one on `/y` is the backstop for the layout itself.
 */
const inShell = (path: string, Component: React.ComponentType): RouteObject => ({
  path,
  element: page(Component),
  errorElement: <RouteError />,
});

export const routes: RouteObject[] = [
  // Dev-only: the live design-system reference. Never registered in production.
  ...(TokenGallery ? [{ path: "/dev/tokens", element: page(TokenGallery) }] : []),
  ...(HierarchyHarness
    ? [{ path: "/dev/hierarchy", element: page(HierarchyHarness) }]
    : []),
  ...(LoopsHarness ? [{ path: "/dev/loops", element: page(LoopsHarness) }] : []),
  ...(AppHarness ? [{ path: "/dev/app", element: page(AppHarness) }] : []),
  /*
    The public routes fail into the One Sheet world they belong to; the /y
    routes below keep the app's own error screen inside the shell.
  */
  {
    path: "/",
    element: <App />,
    errorElement: <RouteError world="sheet" />,
  },
  { path: "/login", element: page(LoginPage), errorElement: <RouteError world="sheet" /> },
  { path: "/signup", element: page(SignUpPage), errorElement: <RouteError world="sheet" /> },
  {
    path: "/signup/phone",
    element: page(SignUpPhonePage),
    errorElement: <RouteError world="sheet" />,
  },
  {
    path: "/password-reset",
    element: page(ResetPasswordPage),
    errorElement: <RouteError world="sheet" />,
  },
  {
    path: "/forgot-passkey",
    element: page(ForgotPasskeyPage),
    errorElement: <RouteError world="sheet" />,
  },
  {
    path: "/y",
    element: page(MenuLayout),
    errorElement: <RouteError />,
    children: [
      inShell("strings", StringsPanel),
      inShell("strings/new", NewStringPage),
      inShell("strings/:id", StringChatPage),
      inShell("knots", KnotsPanel),
      inShell("knots/create", CreateKnotPage),
      inShell("knots/:id", KnotChatPage),
      inShell("knots/:id/info", KnotInfoPage),
      inShell("loops", LoopsPanel),
      inShell("loops/new", NewLoopPage),
      inShell("loops/:id", LoopChatPage),
      // Kept for links shared before the two surfaces merged — see ADR 0009.
      inShell("loops/:id/chat", LoopChatRedirect),
      inShell("b/:handle", BraneSendPage),
      inShell("inbox", InboxPage),
      inShell("notifications", NotificationLayout),
      {
        path: "settings",
        element: page(SettingsLayout),
        errorElement: <RouteError />,
        children: [
          /*
            Bare /y/settings had no page: the sidebar rendered beside nothing on a
            desktop. It opens on the first section instead.
          */
          {
            index: true,
            element: <Navigate to={settingsPath("profile")} replace />,
            errorElement: <RouteError />,
          },
          inShell(SETTINGS_SECTIONS.profile, ProfileSettings),
          inShell(SETTINGS_SECTIONS.branes, BraneSettings),
          inShell(SETTINGS_SECTIONS.devices, DeviceSettings),
          inShell(SETTINGS_SECTIONS.notifications, NotificationsForm),
          inShell(SETTINGS_SECTIONS.subscriptions, BillingSettings),
          inShell(SETTINGS_SECTIONS.appearance, AppearanceSettings),
          inShell(SETTINGS_SECTIONS.blocked, BlockedSettings),
        ],
      },
    ],
  },
  { path: "/b/:handle", element: page(BranePage), errorElement: <RouteError world="sheet" /> },
  { path: "/k/:code", element: page(KnotPage), errorElement: <RouteError world="sheet" /> },
  { path: "/about", element: page(AboutPage), errorElement: <RouteError world="sheet" /> },
  { path: "/faq", element: page(FaqPage), errorElement: <RouteError world="sheet" /> },
  { path: "*", element: <NotFoundPage /> },
];
